import { useSnackbar } from 'notistack';
import { useStringsContext } from '../../../../contexts/strings';
import { useCallback } from 'react';
import { useUserAuth } from '../../../../contexts/auth';
import adminApi from '../../../../api/admin-api';
import { Article, CreateArticleInput, UpdateArticleInput } from '../types';

export const useArticles = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { strings } = useStringsContext();
  const { currentUser } = useUserAuth();

  const createArticle = useCallback(
    async (article: CreateArticleInput): Promise<string | null> => {
      try {
        const response = await adminApi.post({
          path: `/articles`,
          auth: await currentUser?.getIdToken(),
          body: article,
        });

        if (!response.ok) {
          throw new Error(`Creating article failed! (${response.status})`);
        }

        return await response.json();
      } catch (error) {
        console.error(error);

        enqueueSnackbar(strings.messages.loadingError, {
          variant: 'error',
        });
      }
      return null;
    },
    [enqueueSnackbar, strings, currentUser]
  );

  const updateArticle = useCallback(
    async (article: UpdateArticleInput) => {
      try {
        const response = await adminApi.put({
          path: `/articles/${article.id}`,
          auth: await currentUser?.getIdToken(),
          body: article,
        });
        if (!response.ok) {
          throw new Error(`Updating article failed! (${response.status})`);
        }
      } catch (error) {
        console.error(error);

        enqueueSnackbar(strings.messages.loadingError, {
          variant: 'error',
        });
      }
    },
    [enqueueSnackbar, strings, currentUser]
  );

  const fetchArticles = useCallback(
    async (abbrv: string): Promise<Article[]> => {
      let articles: Article[] = [];

      try {
        const response = await adminApi.get({
          path: `/articles/${abbrv}`,
          auth: await currentUser?.getIdToken(),
        });

        if (response.status === 404) {
          return [];
        }

        if (!response.ok) {
          throw new Error(`Fetching articles failed! (${response.status})`);
        }

        articles = await response.json();
      } catch (error) {
        console.error(error);

        enqueueSnackbar(strings.messages.loadingError, {
          variant: 'error',
        });
      }

      return articles;
    },
    [enqueueSnackbar, strings, currentUser]
  );

  return { fetchArticles, createArticle, updateArticle };
};
